import React, { useState, useEffect, useContext } from 'react';
import { Container, Card, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { 
  FaTicketAlt, 
  FaCheckCircle, 
  FaSpinner, 
  FaExclamationCircle,
  FaPauseCircle,
  FaUsers,
  FaBuilding,
  FaProjectDiagram,
  FaEye,
  FaPlus,
  FaChartBar,
  FaTools,
  FaInbox,
  FaUserTie
} from 'react-icons/fa';
import { AuthContext } from '../context/AuthContext';
import reportService from '../services/reportService';
import { formatDate, formatTime, formatNumber } from '../utils/format';
import { translateIssueType, translateIssueTypes } from '../utils/issueTypes';
import { isViewer, canWriteTickets } from '../utils/rbac';

const Dashboard = () => {
  const { user } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const data = await reportService.getDashboard();
      setStats(data);
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'فشل تحميل الإحصائيات';
      setError(errorMessage);
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      new: { label: 'جديدة', cls: 'db-status-new' },
      in_progress: { label: 'قيد العمل', cls: 'db-status-progress' },
      on_hold: { label: 'معلقة', cls: 'db-status-hold' },
      resolved: { label: 'محلولة', cls: 'db-status-resolved' }
    };
    const c = config[status] || { label: status, cls: 'db-status-new' };
    return <span className={`db-status-badge ${c.cls}`}>{c.label}</span>;
  };

  const getPriorityBadge = (priority) => {
    const config = {
      urgent: { label: 'عاجل', cls: 'db-pri-urgent' },
      high:   { label: 'عالي', cls: 'db-pri-high' },
      medium: { label: 'متوسط', cls: 'db-pri-medium' },
      low:    { label: 'عادي', cls: 'db-pri-low' }
    };
    const c = config[priority] || { label: priority, cls: 'db-pri-low' };
    return <span className={`db-priority-badge ${c.cls}`}>{c.label}</span>;
  };

  const renderIssueTypes = (typesArray) => translateIssueTypes(typesArray);

  // Loading Skeleton Component
  const DashboardSkeleton = () => (
    <div className="dashboard-skeleton">
      <div className="skeleton-header">
        <div className="skeleton-title"></div>
        <div className="skeleton-button"></div>
      </div>
      <div className="stats-grid-skeleton">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="skeleton-stat-card">
            <div className="skeleton-icon"></div>
            <div className="skeleton-text"></div>
          </div>
        ))}
      </div>
      <div className="content-grid-skeleton">
        <div className="skeleton-table-card"></div>
        <div className="skeleton-side-cards"></div>
      </div>
    </div>
  );

  // Empty State Component
  const EmptyState = ({ icon: Icon, title, description }) => (
    <div className="dashboard-empty-state">
      <div className="empty-state-icon">
        <Icon size={48} />
      </div>
      <h5>{title}</h5>
      <p>{description}</p>
    </div>
  );

  if (loading) {
    return (
      <Container fluid className="dashboard-modern">
        <DashboardSkeleton />
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="dashboard-modern">
        <div className="dashboard-error-state">
          <div className="error-icon">
            <FaExclamationCircle size={64} />
          </div>
          <h3>خطأ في تحميل البيانات</h3>
          <p>{error}</p>
          <Button className="btn-retry" onClick={() => { setLoading(true); setError(''); fetchStats(); }}>
            إعادة المحاولة
          </Button>
        </div>
      </Container>
    );
  }

  if (!stats) {
    return (
      <Container fluid className="dashboard-modern">
        <EmptyState 
          icon={FaInbox}
          title="لا توجد بيانات"
          description="لا توجد بيانات متاحة لعرضها في الوقت الحالي"
        />
      </Container>
    );
  }

  const isTechnician = user?.role === 'technician';
  const resolvedCount = stats.ticketsByStatus.find(s => s.status === 'resolved')?.count || 0;
  const inProgressCount = stats.ticketsByStatus.find(s => s.status === 'in_progress')?.count || 0;
  const newCount = stats.ticketsByStatus.find(s => s.status === 'new')?.count || 0;
  const onHoldCount = stats.ticketsByStatus.find(s => s.status === 'on_hold')?.count || 0;

  // All KPI Cards configuration - unified grid
  const kpiCards = [
    { 
      id: 'total',
      label: 'إجمالي التذاكر', 
      count: stats.totalTickets, 
      icon: FaTicketAlt, 
      color: '#6366f1',
      bg: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
      textColor: '#ffffff'
    },
    { 
      id: 'new',
      label: 'جديدة', 
      count: newCount, 
      icon: FaExclamationCircle, 
      color: '#ef4444',
      bg: '#ffffff',
      borderColor: '#ef4444',
      accentBg: '#fef2f2',
      textColor: '#ef4444'
    },
    { 
      id: 'in_progress',
      label: 'قيد العمل', 
      count: inProgressCount, 
      icon: FaSpinner, 
      color: '#f59e0b',
      bg: '#ffffff',
      borderColor: '#f59e0b',
      accentBg: '#fffbeb',
      textColor: '#d97706'
    },
    { 
      id: 'on_hold',
      label: 'معلقة', 
      count: onHoldCount, 
      icon: FaPauseCircle, 
      color: '#6b7280',
      bg: '#ffffff',
      borderColor: '#6b7280',
      accentBg: '#f9fafb',
      textColor: '#4b5563'
    },
    { 
      id: 'resolved',
      label: 'محلولة', 
      count: resolvedCount, 
      icon: FaCheckCircle, 
      color: '#10b981',
      bg: '#ffffff',
      borderColor: '#10b981',
      accentBg: '#ecfdf5',
      textColor: '#059669'
    }
  ];

  // Admin KPI cards
  const adminKpiCards = !isTechnician ? [
    { 
      id: 'users',
      label: 'المستخدمين', 
      count: stats.totalUsers ?? 0, 
      icon: FaUsers, 
      color: '#3b82f6',
      bg: '#ffffff',
      borderColor: '#3b82f6',
      accentBg: '#eff6ff',
      textColor: '#2563eb'
    },
    { 
      id: 'branches',
      label: 'الفروع', 
      count: stats.totalBranches ?? 0, 
      icon: FaBuilding, 
      color: '#06b6d4',
      bg: '#ffffff',
      borderColor: '#06b6d4',
      accentBg: '#ecfeff',
      textColor: '#0891b2'
    },
    { 
      id: 'projects',
      label: 'المشاريع', 
      count: stats.projectStats?.total_projects || 0, 
      icon: FaProjectDiagram, 
      color: '#8b5cf6',
      bg: '#ffffff',
      borderColor: '#8b5cf6',
      accentBg: '#f5f3ff',
      textColor: '#7c3aed'
    }
  ] : [];

  const ticketsByType = (stats.ticketsByType || []).slice(0, 10);
  const topBranches = !isTechnician ? (stats.ticketsByBranch || []).slice(0, 10) : [];
  const maxTypeCount = ticketsByType.reduce((max, item) => Math.max(max, Number(item?.count || 0)), 0);
  const maxBranchCount = topBranches.reduce((max, item) => Math.max(max, Number(item?.count || 0)), 0);
  const totalTypeCount = ticketsByType.reduce((sum, item) => sum + Number(item?.count || 0), 0);

  const getBarWidth = (count, max, minPercent = 8) => {
    const safeCount = Number(count || 0);
    if (!max || max <= 0) return `${minPercent}%`;
    return `${Math.max(minPercent, (safeCount / max) * 100)}%`;
  };

  return (
    <Container fluid className="dashboard-modern">
      {/* Header Section */}
      <div className="dashboard-header-modern">
        <div className="header-content">
          <div className="header-icon">
            <FaChartBar />
          </div>
          <div className="header-text">
            <h1>لوحة التحكم</h1>
            <p className="welcome-text">مرحباً بك، {user?.full_name} • {formatDate(new Date(), { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>
        </div>
        {canWriteTickets(user?.role) && (
          <Link to="/tickets/new">
            <Button className="btn-create-modern">
              <FaPlus /> إنشاء تذكرة جديدة
            </Button>
          </Link>
        )}
      </div>

      {/* Primary KPI Cards Grid */}
      <div className="kpi-section">
        <h2 className="section-title">
          <FaTools className="section-icon" />
          إحصائيات التذاكر
        </h2>
        <div className="kpi-grid">
          {kpiCards.map((card) => (
            <div 
              key={card.id} 
              className={`kpi-card ${card.id === 'total' ? 'kpi-card-primary' : ''}`}
              style={{ 
                background: card.bg,
                borderColor: card.borderColor || 'transparent'
              }}
            >
              <div className="kpi-card-content">
                <div 
                  className="kpi-icon-wrapper"
                  style={{ 
                    background: card.id === 'total' ? 'rgba(255,255,255,0.2)' : card.accentBg,
                    color: card.textColor
                  }}
                >
                  <card.icon size={24} />
                </div>
                <div className="kpi-info">
                  <span className="kpi-label" style={{ color: card.id === 'total' ? 'rgba(255,255,255,0.8)' : '#6b7280' }}>
                    {card.label}
                  </span>
                  <span className="kpi-value" style={{ color: card.textColor }}>
                    {formatNumber(card.count)}
                  </span>
                </div>
              </div>
              {card.id === 'total' && <div className="kpi-card-decoration"></div>}
            </div>
          ))}
        </div>
      </div>

      {/* Admin Stats Grid */}
      {!isTechnician && adminKpiCards.length > 0 && (
        <div className="kpi-section admin-section">
          <h2 className="section-title">
            <FaBuilding className="section-icon" />
            إحصائيات النظام
          </h2>
          <div className="kpi-grid admin-kpi-grid">
            {adminKpiCards.map((card) => (
              <div 
                key={card.id} 
                className="kpi-card kpi-card-admin"
                style={{ 
                  background: card.bg,
                  borderColor: card.borderColor
                }}
              >
                <div className="kpi-card-content">
                  <div 
                    className="kpi-icon-wrapper"
                    style={{ 
                      background: card.accentBg,
                      color: card.textColor
                    }}
                  >
                    <card.icon size={22} />
                  </div>
                  <div className="kpi-info">
                    <span className="kpi-label" style={{ color: '#6b7280' }}>
                      {card.label}
                    </span>
                    <span className="kpi-value" style={{ color: card.textColor }}>
                      {formatNumber(card.count)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main Dashboard Content */}
      <div className="dashboard-content-main">

        {/* ── Latest Tickets Table ─────────────────────────────────── */}
        <div className="dbt-section mb-4">
          <Card className="dbt-card">
            <Card.Header className="dbt-card-header">
              <div className="dbt-header-left">
                <div className="dbt-header-icon-wrap">
                  <FaTicketAlt className="dbt-header-icon" />
                </div>
                <div>
                  <span className="dbt-header-title">أحدث التذاكر</span>
                  <span className="dbt-header-subtitle">آخر البلاغات المسجلة في النظام</span>
                </div>
              </div>
              <div className="dbt-header-right">
                <span className="dbt-count-pill">{formatNumber(stats.recentTickets.length)} تذكرة</span>
                <Link to="/tickets" className="dbt-view-all-btn" title="عرض جميع التذاكر">
                  <FaEye size={12} />
                  <span>عرض الكل</span>
                </Link>
              </div>
            </Card.Header>
            <Card.Body className="dbt-card-body p-0">
              {stats.recentTickets.length === 0 ? (
                <div className="dbt-empty">
                  <FaInbox size={40} className="dbt-empty-icon" />
                  <p className="dbt-empty-text">لا توجد تذاكر حديثة</p>
                </div>
              ) : (
                <div className="dbt-scroll-wrap responsive-table-wrapper">
                  <table className="dbt-table responsive-dashboard-table">
                    <thead>
                      <tr>
                        <th className="dbt-th dbt-col-num">رقم التذكرة</th>
                        <th className="dbt-th dbt-col-subject">الموضوع</th>
                        <th className="dbt-th dbt-col-branch">الفرع</th>
                        <th className="dbt-th dbt-col-type">النوع</th>
                        <th className="dbt-th dbt-col-priority">الأولوية</th>
                        <th className="dbt-th dbt-col-status">الحالة</th>
                        <th className="dbt-th dbt-col-tech">الموظف المختص</th>
                        <th className="dbt-th dbt-col-date">تاريخ الإنشاء</th>
                        <th className="dbt-th dbt-col-actions text-center">إجراءات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.recentTickets.map((ticket, idx) => (
                        <tr key={ticket.id} className={`dbt-row ${idx % 2 === 1 ? 'dbt-row-alt' : ''}`}>
                          <td className="dbt-td" data-label="رقم التذكرة">
                            <Link to={`/tickets/${ticket.id}`} className="dbt-ticket-num">
                              #{ticket.ticket_number}
                            </Link>
                          </td>
                          <td className="dbt-td" data-label="الموضوع">
                            <span className="dbt-subject" title={ticket.short_description}>
                              {ticket.short_description}
                            </span>
                          </td>
                          <td className="dbt-td" data-label="الفرع">
                            <span className="dbt-branch-text" title={ticket.branch_name}>
                              {ticket.branch_name}
                            </span>
                          </td>
                          <td className="dbt-td" data-label="النوع">
                            <span className="dbt-type-badge" title={renderIssueTypes(ticket.issue_types)}>
                              {renderIssueTypes(ticket.issue_types)}
                            </span>
                          </td>
                          <td className="dbt-td" data-label="الأولوية">
                            {getPriorityBadge(ticket.priority)}
                          </td>
                          <td className="dbt-td" data-label="الحالة">
                            {getStatusBadge(ticket.status)}
                          </td>
                          <td className="dbt-td" data-label="الموظف المختص">
                            {ticket.assigned_to_name ? (
                              <span className="dbt-tech-name" title={ticket.assigned_to_name}>
                                <FaUserTie size={11} className="dbt-tech-icon" />
                                {ticket.assigned_to_name}
                              </span>
                            ) : (
                              <span className="dbt-unassigned-badge">غير مسندة</span>
                            )}
                          </td>
                          <td className="dbt-td" data-label="تاريخ الإنشاء">
                            <span className="dbt-date" title={`${formatDate(ticket.created_at)} ${formatTime(ticket.created_at, { second: undefined })}`}>
                              {formatDate(ticket.created_at)}
                            </span>
                          </td>
                          <td className="dbt-td text-center table-cell-actions" data-label="إجراءات">
                            <Button
                              as={Link}
                              to={`/tickets/${ticket.id}`}
                              size="sm"
                              className="dbt-action-btn"
                              title="عرض التفاصيل"
                            >
                              <FaEye size={12} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card.Body>
          </Card>
        </div>

        {/* ── Secondary widgets row ─────────────────────────────────── */}
        <div className="dashboard-secondary-row">

          {/* Tickets by Type */}
          <Card className="dbt-card widget-card">
            <Card.Header className="dbt-card-header">
              <div className="dbt-header-left">
                <div className="dbt-header-icon-wrap dbt-icon-chart">
                  <FaChartBar className="dbt-header-icon" />
                </div>
                <div>
                  <span className="dbt-header-title">التذاكر حسب النوع</span>
                </div>
              </div>
            </Card.Header>
            <Card.Body className="dbt-card-body p-0">
              {ticketsByType.length === 0 ? (
                <div className="dbt-empty">
                  <FaInbox size={32} className="dbt-empty-icon" />
                  <p className="dbt-empty-text">لا توجد بيانات</p>
                </div>
              ) : (
                <div className="dbt-scroll-wrap responsive-table-wrapper">
                  <table className="dbt-table responsive-dashboard-table">
                    <thead>
                      <tr>
                        <th className="dbt-th" style={{ width: '40%' }}>النوع</th>
                        <th className="dbt-th text-center" style={{ width: '18%' }}>العدد</th>
                        <th className="dbt-th text-center" style={{ width: '12%' }}>النسبة</th>
                        <th className="dbt-th" style={{ width: '30%' }}>التوزيع</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ticketsByType.map((type, index) => {
                        const pct = totalTypeCount > 0 ? Math.round((type.count / totalTypeCount) * 100) : 0;
                        const barW = getBarWidth(type.count, maxTypeCount, 6);
                        return (
                          <tr key={type.issue_type} className={`dbt-row ${index % 2 === 1 ? 'dbt-row-alt' : ''}`}>
                            <td className="dbt-td" data-label="النوع">
                              <div className="dbt-type-row">
                                <span className={`dbt-rank-dot dbt-rank-dot-${Math.min(index + 1, 5)}`}></span>
                                <span className="dbt-type-label" title={translateIssueType(type.issue_type)}>
                                  {translateIssueType(type.issue_type)}
                                </span>
                              </div>
                            </td>
                            <td className="dbt-td text-center" data-label="العدد">
                              <span className="dbt-count-num">{formatNumber(type.count)}</span>
                            </td>
                            <td className="dbt-td text-center" data-label="النسبة">
                              <span className="dbt-pct-text">{pct}%</span>
                            </td>
                            <td className="dbt-td" data-label="التوزيع">
                              <div className="dbt-bar-track">
                                <div className="dbt-bar-fill dbt-bar-type" style={{ width: barW }}></div>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Top Branches */}
          {!isTechnician && topBranches.length > 0 && (
            <Card className="dbt-card widget-card">
              <Card.Header className="dbt-card-header">
                <div className="dbt-header-left">
                  <div className="dbt-header-icon-wrap dbt-icon-branch">
                    <FaBuilding className="dbt-header-icon" />
                  </div>
                  <div>
                    <span className="dbt-header-title">أكثر الفروع نشاطاً</span>
                  </div>
                </div>
              </Card.Header>
              <Card.Body className="dbt-card-body p-0">
                <div className="dbt-scroll-wrap responsive-table-wrapper">
                  <table className="dbt-table responsive-dashboard-table">
                    <thead>
                      <tr>
                        <th className="dbt-th text-center" style={{ width: '8%' }}>#</th>
                        <th className="dbt-th" style={{ width: '34%' }}>الفرع</th>
                        <th className="dbt-th text-center" style={{ width: '14%' }}>الإجمالي</th>
                        <th className="dbt-th text-center" style={{ width: '14%' }}>مفتوحة</th>
                        <th className="dbt-th text-center" style={{ width: '14%' }}>محلولة</th>
                        <th className="dbt-th" style={{ width: '16%' }}>النشاط</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topBranches.map((branch, index) => (
                        <tr key={branch.name} className={`dbt-row ${index % 2 === 1 ? 'dbt-row-alt' : ''} ${index === 0 ? 'dbt-row-top' : ''}`}>
                          <td className="dbt-td text-center" data-label="الترتيب">
                            <span className={`dbt-rank-pill ${index < 3 ? `dbt-rank-${index + 1}` : 'dbt-rank-n'}`}>
                              {index + 1}
                            </span>
                          </td>
                          <td className="dbt-td" data-label="الفرع">
                            <span className="dbt-branch-text" title={branch.name}>{branch.name}</span>
                          </td>
                          <td className="dbt-td text-center" data-label="الإجمالي">
                            <span className="dbt-count-num">{formatNumber(branch.count)}</span>
                          </td>
                          <td className="dbt-td text-center" data-label="مفتوحة">
                            <span className="dbt-open-count">{formatNumber(branch.open_count || 0)}</span>
                          </td>
                          <td className="dbt-td text-center" data-label="محلولة">
                            <span className="dbt-resolved-count">{formatNumber(branch.resolved_count || 0)}</span>
                          </td>
                          <td className="dbt-td" data-label="النشاط">
                            <div className="dbt-bar-track">
                              <div
                                className="dbt-bar-fill dbt-bar-branch"
                                style={{ width: getBarWidth(branch.count, maxBranchCount, 10) }}
                              ></div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card.Body>
            </Card>
          )}
        </div>
      </div>

    </Container>
  );
};

export default Dashboard;
